import { useEffect, useRef, useState } from 'react';
import { mount } from 'pele';
import { z } from 'zod';
import type { Embed, Section } from '@pen/shared';
import { request } from '../api';
import { message, Rich } from '../ui/hooks';
import { loadEmbed, observeActs, pauseEmbeds } from './embed';
import type { ActProgress } from './embed';

export function EmbedSection({ embed, label, onAct }: { embed: Embed; label: string; onAct(progress: ActProgress): void }) {
  return embed.frame ? <FrameEmbedSection embed={embed} label={label} onAct={onAct} /> : <InlineEmbedSection embed={embed} onAct={onAct} />;
}

function FrameEmbedSection({ embed, label, onAct }: { embed: Embed; label: string; onAct(progress: ActProgress): void }) {
  const ref = useRef<HTMLIFrameElement>(null);
  const listener = useRef(onAct);
  listener.current = onAct;
  const cleanup = useRef<(() => void) | undefined>(undefined);
  const [near, setNear] = useState(false);
  const [height, setHeight] = useState(320);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    const observer = new IntersectionObserver(entries => {
      if (!entries.some(entry => entry.isIntersecting)) return;
      setNear(true);
      observer.disconnect();
    }, { rootMargin: '600px' });
    observer.observe(ref.current!);
    return () => observer.disconnect();
  }, []);
  useEffect(() => () => cleanup.current?.(), [embed]);
  const query = new URLSearchParams({ page: embed.frame!.page, host: embed.frame!.hostId });
  return <>{error && <p className="error" role="alert">{error}</p>}<iframe className="embed-frame" ref={ref} title={label} src={near ? `/embed-frame?${query}` : undefined} loading="lazy" style={{ height }} onError={() => setError(`Could not load ${label}.`)} onLoad={() => {
    cleanup.current?.();
    if (!near) return;
    const doc = ref.current?.contentDocument;
    if (!doc?.body) return;
    setError(null);
    let pending = 0;
    let disposed = false;
    const measure = () => {
      pending = 0;
      const body = doc.body;
      const style = doc.defaultView!.getComputedStyle(body);
      const next = Math.ceil(Math.max(body.getBoundingClientRect().height, body.scrollHeight) + (parseFloat(style.marginTop) || 0) + (parseFloat(style.marginBottom) || 0));
      if (next > 0) setHeight(previous => previous === next ? previous : next);
    };
    const schedule = () => { if (!disposed && !pending) pending = requestAnimationFrame(measure); };
    const observer = new ResizeObserver(schedule);
    observer.observe(doc.body);
    window.addEventListener('resize', schedule);
    const stopActs = observeActs(doc, embed.acts, progress => listener.current(progress));
    measure();
    cleanup.current = () => {
      disposed = true;
      cancelAnimationFrame(pending);
      observer.disconnect();
      window.removeEventListener('resize', schedule);
      stopActs();
      pauseEmbeds(doc);
    };
  }} /></>;
}

function InlineEmbedSection({ embed, onAct }: { embed: Embed; onAct(progress: ActProgress): void }) {
  const ref = useRef<HTMLDivElement>(null);
  const listener = useRef(onAct);
  listener.current = onAct;
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    const host = ref.current!;
    let disposed = false;
    let cleanup: (() => void) | undefined;
    void loadEmbed(host, embed).then(loaded => {
      if (disposed) { loaded.destroy(); return; }
      const off = loaded.onAct(progress => listener.current(progress));
      cleanup = () => { off(); loaded.destroy(); };
    }).catch(reason => { if (!disposed) setError(message(reason)); });
    return () => { disposed = true; cleanup?.(); };
  }, [embed]);
  return <>{error && <p className="error" role="alert">{error}</p>}<div className="embed-host" ref={ref} /></>;
}
export function PeleDiagram({ source }: { source: string }) {
  const ref = useRef<HTMLDivElement>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    setError(null);
    const diagram = mount(ref.current!, source, { links: false, images: false, zoom: 'auto', onError: reason => setError(message(reason)) });
    return () => diagram.destroy();
  }, [source]);
  return <>{error && <p className="error" role="alert">Diagram: {error}</p>}<div className="pele-diagram" ref={ref} role="img" aria-label="Lesson diagram" /></>;
}
export function GuessSection({ section, lessonId }: { section: Extract<Section, { kind: 'guess' }>; lessonId: string }) {
  const [choice, setChoice] = useState<number | null>(null);
  const [result, setResult] = useState<boolean | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  return <div className="guess"><p className="kicker">Before you look</p><p className="reading"><Rich html={section.question} /></p><div className="guess-options">{section.options.map((option, index) => <button key={index} disabled={busy || result !== null} aria-pressed={choice === index} onClick={() => setChoice(index)}><span>{String.fromCharCode(65 + index)}.</span><Rich html={option} /></button>)}</div>
    {result === null ? <button disabled={choice === null || busy} onClick={() => {
      if (choice === null) return;
      setBusy(true); setError(null);
      void request('POST', `/api/lessons/${encodeURIComponent(lessonId)}/guesses/${encodeURIComponent(section.id)}`, { choice }, z.object({ correct: z.boolean() })).then(response => setResult(response.correct)).catch(reason => setError(message(reason))).finally(() => setBusy(false));
    }}>{busy ? 'Checking…' : 'See what happens'}</button> : <div aria-live="polite"><p className={result ? 'success' : 'muted'}>{result ? 'That’s the idea.' : 'Here’s the distinction.'}</p><p className="reading"><Rich html={section.explanation} /></p></div>}
    {error && <p className="error" role="alert">{error}</p>}
  </div>;
}
